import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../auth/store.js';
import { countFailed, countWaiting } from '../photos/index.js';
import { listRecent, retryAllRejected, retryRecord, syncNow, useSyncStore } from '../sync/index.js';
import type { OutboxRecord, OutboxStatus } from '../sync/index.js';

const STATUS_LABEL: Record<OutboxStatus, string> = {
  pending: 'Ждёт отправки',
  sending: 'Отправляется',
  synced: 'Отправлено',
  rejected: 'Отклонено сервером',
};

const STATUS_TONE: Record<OutboxStatus, string> = {
  pending: 'bg-amber-500 text-black',
  sending: 'bg-sky-600 text-white',
  synced: 'bg-emerald-700 text-white',
  rejected: 'bg-red-600 text-white',
};

function formatTime(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function QueueRow({ record }: { record: OutboxRecord }): JSX.Element {
  return (
    <li className="rounded-xl bg-card shadow-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-touch">
        <span className={`rounded-lg px-3 py-1 text-base font-semibold ${STATUS_TONE[record.status]}`}>
          {STATUS_LABEL[record.status]}
        </span>
        <span className="text-base text-dim">{formatTime(record.createdAt)}</span>
      </div>

      <p className="mt-3 text-base text-body">{record.event.type}</p>

      {record.attempts > 0 && record.status !== 'synced' && (
        <p className="mt-1 text-base text-dim">Попыток отправки: {record.attempts}</p>
      )}
      {record.rejectionReason && (
        <p className="mt-2 text-base text-red-300">Причина: {record.rejectionReason}</p>
      )}
      {record.lastError && record.status === 'pending' && (
        <p className="mt-2 text-base text-dim">Последняя ошибка: {record.lastError}</p>
      )}

      {record.status === 'rejected' && (
        <button
          type="button"
          onClick={() => void retryRecord(record.id)}
          className="mt-3 rounded-xl bg-sky-600 px-5 py-3 text-base font-semibold text-white active:bg-sky-700"
        >
          Повторить
        </button>
      )}
    </li>
  );
}

/**
 * Экран очереди. Показывает всё, что не ушло, и позволяет повторить вручную.
 * Ничего не удаляется — оператор всегда видит судьбу своего отчёта.
 */
export function QueueScreen(): JSX.Element {
  const records = useLiveQuery(() => listRecent(200), [], [] as OutboxRecord[]);
  const photos = useLiveQuery(
    async () => ({ waiting: await countWaiting(), failed: await countFailed() }),
    [],
    { waiting: 0, failed: 0 },
  );
  const phase = useSyncStore((state) => state.phase);
  const lastError = useSyncStore((state) => state.lastError);
  const nextAttemptAt = useSyncStore((state) => state.nextAttemptAt);
  const requireReauth = useAuthStore((state) => state.requireReauth);
  const [busy, setBusy] = useState(false);

  const unsent = records.filter((r) => r.status === 'pending' || r.status === 'sending');
  // Отклонённые показываем только свои: очередь общая для устройства, а
  // разобрать чужой отказ всё равно может только тот, кто его создал
  const me = useAuthStore((state) => state.user?.id);
  const rejected = records.filter((r) => r.status === 'rejected' && r.event.userId === me);

  const handleSync = async (): Promise<void> => {
    setBusy(true);
    try {
      await syncNow();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Очередь отправки</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      <section className="mb-4 rounded-xl bg-card shadow-card p-4">
        <p className="text-counter font-semibold">{unsent.length}</p>
        <p className="text-base text-dim">записей ждут отправки</p>
        {rejected.length > 0 && (
          <p className="mt-2 text-base text-red-300">Отклонено сервером: {rejected.length}</p>
        )}
        {photos.waiting > 0 && (
          <p className="mt-2 text-base text-body">
            Фотографии ждут загрузки: {photos.waiting}. Отчёт это не задерживает.
          </p>
        )}
        {photos.failed > 0 && (
          <p className="mt-2 text-base text-red-300">Фотографии с ошибкой: {photos.failed}</p>
        )}
        {nextAttemptAt && phase === 'waiting' && (
          <p className="mt-2 text-base text-dim">
            Следующая попытка в {formatTime(nextAttemptAt)}
          </p>
        )}
        {lastError && <p className="mt-2 text-base text-dim">{lastError}</p>}

        {phase === 'unauthorized' && (
          <button
            type="button"
            onClick={() => requireReauth()}
            className="mt-4 w-full rounded-xl bg-amber-500 px-5 py-4 text-lg font-semibold text-black active:bg-amber-600"
          >
            Ввести PIN и отправить
          </button>
        )}

        <div className="mt-4 flex flex-wrap gap-touch">
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSync()}
            className="rounded-xl bg-sky-600 px-5 py-3 text-base font-semibold text-white active:bg-sky-700 disabled:opacity-50"
          >
            {busy ? 'Отправляю…' : 'Отправить сейчас'}
          </button>
          {rejected.length > 0 && (
            <button
              type="button"
              onClick={() => void retryAllRejected()}
              className="rounded-xl bg-muted px-5 py-3 text-base font-semibold active:bg-line"
            >
              Повторить все отклонённые
            </button>
          )}
        </div>
      </section>

      {records.length === 0 ? (
        <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">
          Очередь пуста — всё отправлено.
        </p>
      ) : (
        <ul className="flex flex-col gap-touch">
          {records.map((record) => (
            <QueueRow key={record.id} record={record} />
          ))}
        </ul>
      )}
    </div>
  );
}
